import Decimal from "decimal.js";
import { MORTGAGE_TYPE_ENUM } from "../constants";
import { FIXED_LOAN_AMOUNT } from "../../constants/payment_type";
import { MONTHLY } from "../../constants/frequencies";
import { REFINANCE } from "../../constants/document_types";
import { YES, NO } from "../../constants/yes_no";
import { PURCHASE } from "../../constants/loan_purpose";
import {
  ARM_TERMS,
  ARM_3_6, ARM_5_6, ARM_7_6, ARM_10_6
} from "../../constants/loan_terms";

// Reconstruct the LANIS ARM product from the exported fixed period. Defaults to
// the modern "/6" (SOFR) ARMs, which is what's originated today; legacy "/1"
// ARMs would need Phase 2 (the full margin/index/caps mapping).
const ARM_YEARS_TO_TERM = {
  3: ARM_3_6,
  5: ARM_5_6,
  7: ARM_7_6,
  10: ARM_10_6
};

export function importLoanDetail(obj, { document, loan }) {
  if (obj.ApplicationReceivedDate) {
    document.applicant.first_contact_date = obj.ApplicationReceivedDate;
  }
  if (loan && obj.InitialFixedPeriodEffectiveMonthsCount != null) {
    const months = parseInt(obj.InitialFixedPeriodEffectiveMonthsCount, 10);
    if (!isNaN(months)) {
      const armTerm = ARM_YEARS_TO_TERM[Math.round(months / 12)];
      if (armTerm) {
        loan.term = armTerm;
        loan.arm_years = "30";
      }
    }
  }
}

export function importTermsOfLoan(obj, { loan, options }) {
  // Purpose: only Purchase maps 1:1. MISMO's "Refinance" doesn't say which
  // LANIS refinance purpose (cash-out vs rate/term, etc.), so the LO picks it.
  if (
    obj.LoanPurposeType === "Purchase" &&
    !(options && options.documentType === REFINANCE)
  ) {
    loan.purpose = PURCHASE;
  }
  if (obj.MortgageType) {
    for (const [id, label] of Object.entries(MORTGAGE_TYPE_ENUM)) {
      if (label === obj.MortgageType) {
        loan.type = id;
        break;
      }
    }
  }
  // Point sends BaseLoanAmount (NoteAmount is the fallback). Set it as a fixed
  // loan amount so it shows on the Loan Configurator (down payment = price - amt).
  const amount =
    obj.BaseLoanAmount != null ? obj.BaseLoanAmount : obj.NoteAmount;
  if (amount != null) {
    loan.fixed_loan_amount = String(amount);
    loan.down_payment_type = FIXED_LOAN_AMOUNT;
  }
  if (obj.NoteRatePercent != null) {
    loan.rate = String(obj.NoteRatePercent);
  }
}

export function importRefinance(obj, { loan }) {
  if (obj.RefinanceCashOutDeterminationType != null) {
    loan.rate_and_term_refi =
      obj.RefinanceCashOutDeterminationType === "CashOut" ? NO : YES;
  }
}

export function importAmortizationRule(obj, { loan }) {
  // ARM term was already set from the fixed period in LOAN_DETAIL (imported
  // first); don't overwrite it with the plain amortization years.
  if (ARM_TERMS.includes(loan.term)) return;
  if (!obj.LoanAmortizationPeriodCount) return;
  const count = parseInt(obj.LoanAmortizationPeriodCount, 10);
  if (isNaN(count)) return;
  if (obj.LoanAmortizationPeriodType && obj.LoanAmortizationPeriodType !== "Month") return;
  const years = Math.round(count / 12);
  loan.term = String(years);
}

// Proposed monthly housing expenses -> subject property tax / insurance / HOA.
// Purchase and Refinance store tax/insurance differently, so the loan type
// (from the Import File menu) is passed through options.
export function importHousingExpense(obj, { document, options }) {
  if (
    obj.HousingExpenseTimingType &&
    obj.HousingExpenseTimingType !== "Proposed"
  ) {
    return;
  }
  const amount = obj.HousingExpensePaymentAmount;
  if (amount == null) return;
  const property = document.property;
  const isRefinance = options && options.documentType === REFINANCE;
  const annual = new Decimal(amount).mul(12).toString();
  switch (obj.HousingExpenseType) {
    case "RealEstateTax":
      if (isRefinance) {
        // Refinance tax field is a dollar amount + frequency (monthly here).
        property.refinance_property_tax_amount = String(amount);
        property.refinance_property_tax_frequency = MONTHLY;
      } else {
        // Purchase tax uses the "Amount" toggle with the annual dollar figure.
        property.property_tax_type = "amount";
        property.property_tax_annual_amount = annual;
      }
      break;
    case "HomeownersInsurance":
      if (isRefinance) {
        property.home_owners_insurance = String(amount);
        property.home_owners_insurance_frequency = MONTHLY;
      } else {
        // Purchase insurance field is treated as an annual amount.
        property.home_owners_insurance = annual;
      }
      break;
    case "HomeownersAssociationDuesAndCondominiumFees":
      property.hoa1 = String(amount);
      property.hoa1_frequency = MONTHLY;
      break;
    default:
      break;
  }
}
